import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import ModalMobile from "../../../../components/shared/Modal/ModalMobile";
import Button from "../../../../components/shared/Button/Button";
import Divider from "../../../../components/shared/Divider/Divider";
import Input from "../../../../components/shared/Input/Input";
import Select from "../../../../components/shared/Select/Select";
import Checkbox from "../../../../components/shared/Checkbox";
import Spinner from "../../../../components/shared/spinner/Spinner";
import { userService } from "../../../../services/user";
import { listOfSubscriptions } from "../../../../common/envConfig";
import {
  getHeightOptions,
  normalizeHeight,
} from "../../../../components/shared/utils/heightOptions";
import { adminSearchService } from "../../../../services/adminSearch";
import { useInvalidate } from "../../../../hooks/useInvalidate";
import { useAdminSearchUsers } from "../../hooks/useAdminSearchUsers";
import { useAdminSearchStore } from "../../store/adminSearchStore";
import { toast } from "react-toastify";

const genderOptions = [
  { text: "Male", value: "Male" },
  { text: "Female", value: "Female" },
  { text: "Prefer Not to Answer", value: "prefer not to answer" },
];

const dominantHandOptions = [
  { text: "Left", value: "Left" },
  { text: "Right", value: "Right" },
];
const secondarySubTypeOptions = [
  { text: "None", value: "none" },
  { text: "Free", value: listOfSubscriptions.STUDENT_FREE },
  { text: "3D Player", value: listOfSubscriptions.STUDENT_LITE_ANNUALLY },
  { text: "3D Pro", value: listOfSubscriptions.PREMIUM_ANNUALLY },
];
const numberFields = [
  "creditCount",
  "reportCredits",
  "weeklyPractices",
  "averageScore",
];

const stringFields = [
  "firstName",
  "lastName",
  "email",
  "userName",
  "role",
  "dob",
  "gender",
  "dominantHand",
  "secondarySubType",
  "height",
  "hipSize",
  "contactNumber",
];

const mapUserToForm = (user) => {
  const base = {
    firstName: "",
    lastName: "",
    email: "",
    userName: "",
    dob: "",
    gender: null,
    dominantHand: null,
    height: "",
    hipSize: "",
    contactNumber: "",
    secondarySubType: "none",
    creditCount: "",
    reportCredits: "",
    weeklyPractices: "",
    averageScore: "",
    userConsent: false,
  };

  if (!user) return base;

  const fallbackName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return {
    ...base,
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    email: user.email || "",
    userName: user.userName || fallbackName,
    dob: user.dob || user.dobStr || "",
    gender: user.gender || null,
    dominantHand: user.dominantHand || null,
    height: user.height || "",
    hipSize: user.hipSize || "",
    contactNumber: user.contactNumber || "",
    secondarySubType: user.secondarySubType || "none",
    creditCount:
      user.creditCount === 0 || user.creditCount
        ? String(user.creditCount)
        : "",
    reportCredits:
      user.reportCredits === 0 || user.reportCredits
        ? String(user.reportCredits)
        : "",
    weeklyPractices:
      user.weeklyPractices === 0 || user.weeklyPractices
        ? String(user.weeklyPractices)
        : "",
    averageScore:
      user.averageScore === 0 || user.averageScore
        ? String(user.averageScore)
        : "",
    userConsent: Boolean(user.userConsent),
  };
};

const pickDirtyValues = (values, dirty) => {
  if (!dirty || typeof dirty !== "object") return {};

  return Object.keys(dirty).reduce((acc, key) => {
    const isDirty = dirty[key];
    if (isDirty === true) {
      acc[key] = values[key];
    } else if (isDirty && typeof isDirty === "object") {
      const nested = pickDirtyValues(values[key] || {}, isDirty);
      if (Object.keys(nested).length > 0) {
        acc[key] = nested;
      }
    }
    return acc;
  }, {});
};

const normalizePayload = (values) => {
  const payload = { ...values };

  numberFields.forEach((field) => {
    if (!(field in payload)) return;
    const raw = payload[field];
    if (raw === "" || raw === null) {
      payload[field] = null;
      return;
    }
    if (typeof raw === "undefined") return;
    const num = Number(raw);
    if (!Number.isNaN(num)) {
      payload[field] = num;
    } else {
      delete payload[field];
    }
  });

  stringFields.forEach((field) => {
    if (!(field in payload)) return;
    if (typeof payload[field] === "undefined") {
      delete payload[field];
    }
  });

  return payload;
};

export default function EditUserModal({ isOpen, onClose }) {
  const invalidate = useInvalidate();

  const { usersState } = useAdminSearchStore();

  const { selectedUserId } = usersState;

  const { data, isLoading, error } = useAdminSearchUsers();
  const users = data?.users || [];
  const userSummary = users.find((u) => u?.id === selectedUserId);

  const [loadError, setLoadError] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { dirtyFields, isDirty },
  } = useForm({
    defaultValues: mapUserToForm(userSummary),
  });

  const heightOptions = useMemo(
    () =>
      getHeightOptions().map(({ feet, inches }) => ({
        text: `${feet}'${inches}"`,
        value: `${feet}'${inches}"`,
      })),
    [],
  );

  useEffect(() => {
    if (userSummary) {
      reset(mapUserToForm(userSummary));
    }
  }, [userSummary, reset]);

  useEffect(() => {
    if (!isOpen || !selectedUserId) return;
    let active = true;
    const fetchUser = async () => {
      setLoadError(null);
      setLoadingUser(true);
      try {
        const fullUser = await userService.getUserData(selectedUserId);
        if (!active) return;
        reset(mapUserToForm(fullUser || userSummary || {}));
      } catch (err) {
        if (!active) return;
        console.error("Failed to fetch user", err);
        setLoadError(err?.message || "Failed to fetch user details");
        if (userSummary) {
          reset(mapUserToForm(userSummary));
        }
      } finally {
        if (active) setLoadingUser(false);
      }
    };

    fetchUser();
    return () => {
      active = false;
    };
  }, [isOpen, selectedUserId, userSummary, reset]);

  const mutation = useMutation(
    async ({ id, data }) => {
      return adminSearchService.adminSearchUpdateUser({
        id,
        data,
      });
    },
    {
      onSuccess: async () => {
        invalidate(["adminSearchUsers_key"]);
      },
    },
  );

  const genderValue = watch("gender");
  const dominantHandValue = watch("dominantHand");
  const secondarySubTypeValue = watch("secondarySubType");
  const heightValue = normalizeHeight(watch("height"));
  const userConsentValue = watch("userConsent");

  const handleSelectChange = useCallback(
    (field, value, currentValue) => {
      const nextValue = value === currentValue ? null : value;
      setValue(field, nextValue, {
        shouldDirty: true,
        shouldValidate: false,
      });
    },
    [setValue],
  );

  const onSubmit = useCallback(
    async (values) => {
      if (!selectedUserId) return;
      const { ...rest } = values;
      const dirtyValues = pickDirtyValues(rest, dirtyFields);
      const normalized = normalizePayload(dirtyValues);

      if (!Object.keys(normalized).length) {
        toast.info("No changes to save.");
        return;
      }

      try {
        await mutation.mutateAsync({
          id: selectedUserId,
          data: normalized,
        });
        toast.success("Changes saved.");
        onClose?.();
      } catch (err) {
        console.error("Failed to update user", err);
        toast.error(err?.message || "Failed to update user");
      }
    },
    [dirtyFields, mutation, onClose, selectedUserId],
  );

  const isSaving = mutation.isPending;
  const isBusy = isSaving || loadingUser;

  if (!isOpen || !selectedUserId) return null;

  return (
    <ModalMobile
      className="font-manrope w-full max-w-4xl pt-10"
      isOpen={isOpen}
      onClose={onClose}
      showClose={true}
    >
      <div className="px-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-semibold text-gray900">Edit user</div>
            <div className="text-base text-gray600">
              {userSummary?.email || userSummary?.id || selectedUserId}
            </div>
          </div>
          {loadingUser && (
            <div className="flex items-center gap-2 text-base text-gray600">
              <Spinner color="primary500" size="sm" /> Loading latest data…
            </div>
          )}
        </div>

        <Divider className="my-4" color="gray200" type="horizontal" />

        {loadError ? (
          <div className="mb-4 rounded-lg border border-error200 bg-error50 px-4 py-3 text-base text-error700">
            {loadError}
          </div>
        ) : null}

        <form className="flex flex-col gap-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              control={control}
              name="firstName"
              label="First name"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
            <Input
              control={control}
              name="lastName"
              label="Last name"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
            <Input
              control={control}
              name="email"
              label="Email"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
            <Input
              control={control}
              name="userName"
              label="Username"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
            <Input
              control={control}
              name="dob"
              label="Date of birth"
              placeholder="MM/DD/YYYY"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
            <Input
              control={control}
              name="hipSize"
              label="Hip size"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
            <Input
              control={control}
              name="contactNumber"
              label="Contact number"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
            <Input
              control={control}
              name="weeklyPractices"
              label="Weekly practices"
              type="number"
              inputMode="numeric"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
            <Input
              control={control}
              name="averageScore"
              label="Average score"
              type="number"
              inputMode="numeric"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />

            <div className="flex flex-col gap-2">
              <label className="text-base text-gray700">Gender</label>
              <Select
                options={genderOptions}
                value={genderValue || null}
                placeholder="Select gender"
                withCheckbox
                disabled={isBusy}
                buttonProps={{ size: "md" }}
                onChange={(val) =>
                  handleSelectChange("gender", val, genderValue || null)
                }
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-base text-gray700">Dominant hand</label>
              <Select
                options={dominantHandOptions}
                value={dominantHandValue || null}
                placeholder="Select hand"
                withCheckbox
                disabled={isBusy}
                buttonProps={{ size: "md" }}
                onChange={(val) =>
                  handleSelectChange(
                    "dominantHand",
                    val,
                    dominantHandValue || null,
                  )
                }
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-base text-gray700">
                Secondary sub type
              </label>
              <Select
                options={secondarySubTypeOptions}
                value={secondarySubTypeValue || "none"}
                placeholder="Select secondary sub type"
                withCheckbox
                disabled={isBusy}
                buttonProps={{ size: "md" }}
                onChange={(val) =>
                  setValue("secondarySubType", val || "none", {
                    shouldDirty: true,
                    shouldValidate: false,
                  })
                }
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-base text-gray700">Height</label>
              <Select
                options={heightOptions}
                value={heightValue || null}
                placeholder="Select height"
                withCheckbox
                disabled={isBusy}
                buttonProps={{ size: "md" }}
                onChange={(val) =>
                  handleSelectChange("height", val, heightValue || null)
                }
              />
            </div>

            <Input
              control={control}
              name="creditCount"
              label="Credits"
              type="number"
              inputMode="numeric"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
            <Input
              control={control}
              name="reportCredits"
              label="Report credits"
              type="number"
              inputMode="numeric"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <Checkbox
                isChecked={Boolean(userConsentValue)}
                isDisabled={isBusy}
                onChange={(checked) =>
                  setValue("userConsent", checked, {
                    shouldDirty: true,
                    shouldValidate: false,
                  })
                }
              />
              <span className="text-base text-gray800">User consent</span>
            </div>
          </div>
        </form>
      </div>

      <Divider className="mt-6" color="gray200" type="horizontal" />

      <div className="flex items-center justify-between gap-3 px-6 py-4">
        <div className="text-base text-gray600">
          {isSaving && "Saving changes…"}
          {!isSaving && mutation.error
            ? mutation.error?.message || "Failed to update user"
            : null}
        </div>
        <div className="flex gap-2">
          <Button
            color="gray100"
            onClick={onClose}
            size="md"
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            isLoading={isSaving}
            size="md"
            isDisabled={!isDirty || isSaving}
          >
            Save changes
          </Button>
        </div>
      </div>
    </ModalMobile>
  );
}
